import * as React from "react";
import { styled } from "@mui/material/styles";
import { Typography } from "@mui/material";
import MarketPresenceMSATables from "./MarketPresenceMSATable";
import { KeyValue, Query } from "../../../../interfaces";
import { selectedStatesAlpha } from "../../../../refdata/FilterOptions";
import MarketShareStateList from "../../../../containers/MarketShareStateList";

const PREFIX = "MarketPresenceTables";

const classes = {
  body: `${PREFIX}-body`,
  footer: `${PREFIX}-footer`,
  moreInfo: `${PREFIX}-moreInfo`,
  openCloseAll: `${PREFIX}-openCloseAll`,
  footerText: `${PREFIX}-footerText`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.body}`]: {
    color: theme.palette.primary.main,
    fontWeight: 550,
    fontSize: 16,
    lineHeight: "24px",
    fontStyle: "normal",
  },

  [`& .${classes.footer}`]: {
    color: theme.palette.primary.main,
    lineHeight: "24px",
    fontSize: "16px",
    fontWeight: 400,
    textAlign: "left",
  },

  [`& .${classes.moreInfo}`]: {
    "color": "#0000ff",
    "fontWeight": "bold",
    "fontSize": 16,
    "lineHeight": "24px",
    "paddingBottom": "10px",
    "fontStyle": "normal",
    "&:hover, &.Mui-focusVisible": {
      textDecoration: "underline",
      cursor: "pointer",
    },
  },

  [`& .${classes.openCloseAll}`]: {
    "minWidth": 80,
    "margin": 0,
    "color": "#ffffff",
    "fontWeight": 600,
    "backgroundColor": "#426cac",
    "&:hover, &.Mui-focusVisible": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.secondary.main,
    },
  },

  [`& .${classes.footerText}`]: {
    color: theme.palette.primary.main,
    width: 900,
    minWidth: 900,
    fontSize: 16,
  },
}));

interface Props {
  appState: Query;
  searchPills: React.ReactElement;
  updateUi: (key: string, value: string | KeyValue[]) => void;
}

export const appName = (stalpbr: string, displayType: string) => {
  const options: KeyValue = {
    "statesByCounty":
      stalpbr && stalpbr !== "ALL"
        ? `${
            selectedStatesAlpha.find(
              (state: KeyValue) => state.value === stalpbr,
            )?.label
          } by Counties`
        : "Nationally By State & County",
    "msas": "Metropolitan Statistical Areas (MSAs)",
    "": "",
  };
  return options[displayType];
};

export default function MarketPresenceTables({
  appState,
  searchPills,
  updateUi,
}: Props) {
  const [initialLoad, setInitialLoad] = React.useState(true);
  React.useEffect(() => {
    if (initialLoad) {
      window.scrollTo(0, 0);
      setInitialLoad(false);
      !appState.ui.displayResults &&
        updateUi("displayResults", "statesByCounty");
    }
  }, [appState.ui.displayResults, initialLoad, updateUi]);

  const footer1 = (
    <Typography className={classes.footer}>
      *The total deposits for the bank holding company is the combined total
      domestic deposits of the underlying insured subsidiaries.
    </Typography>
  );

  const footer2 = (
    <Typography className={classes.footer}>
      <b>Note about this data for BHCs or Bank Holding Companies:</b>
      <br />
      This search will return results for top regulatory bank holding companies
      and their FDIC-insured subsidiaries. The regulatory top holder is assigned
      by the Federal Reserve Board based on ownership and control percentages.
      Thrift bank holding company information is not available. Official bank
      holding company information can be found by visiting the website of the
      Federal Reserve System - National Information Center.
    </Typography>
  );

  return (
    <Root>
      {appState.ui.displayResults === "statesByCounty" && (
        <MarketShareStateList searchPills={searchPills} />
      )}
      {appState.ui.displayResults === "msas" && (
        <MarketPresenceMSATables
          appState={appState}
          updateUi={updateUi}
          searchPills={searchPills}
        />
      )}
      {appState.filters.bhcRSSD && footer1}
      {appState.filters.bhcRSSD && <br />}
      {appState.filters.bhcRSSD && footer2}
    </Root>
  );
}
